<template>
	<a
		v-if="destinationUrl"
		ref="adContainer"
		:href="clickUrl ?? destinationUrl"
		target="_blank"
		rel="noopener noreferrer sponsored"
		class="nav-link live"
		style="display: flex; text-decoration: none; align-items: center"
	>
		<span class="blob red"></span>
		<span class="lg:inline-block hidden">Watch Live</span>
	</a>
</template>

<script setup>
const AD_SLOT_URL =
	"https://aizen-ad-manager.up.railway.app/api/slots/c1b08f44-c4a6-4401-ba9e-3d066ff0efd2/serve";

const adContainer = ref(null);
const destinationUrl = ref(null);
const clickUrl = ref(null);
const impressionUrl = ref(null);

onMounted(async () => {
	try {
		const ad = await $fetch(AD_SLOT_URL);
		if (ad?.destinationUrl) {
			destinationUrl.value = ad.destinationUrl;
			clickUrl.value = ad.clickUrl ?? null;
			impressionUrl.value = ad.impressionUrl ?? null;
		}
	} catch (error) {
		console.error("[watch-live] Failed to fetch ad:", error);
	}
});

// IAB viewability standard: fire impression only after ≥50% of the element
// has been visible for ≥1 continuous second.
// Uses fetch (GET + keepalive) instead of sendBeacon because sendBeacon sends
// POST, and the impression endpoint only accepts GET.
// flush: 'post' ensures the watcher runs after Vue commits the DOM so
// adContainer.value is the real <a> element (not null from v-if).
watch(
	impressionUrl,
	(url) => {
		if (!url || !adContainer.value) return;

		let fired = false;
		let timer = null;

		const observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					if (entry.isIntersecting && !fired) {
						timer = setTimeout(() => {
							if (!fired) {
								fired = true;
								fetch(url, { keepalive: true }).catch(() => {});
								observer.disconnect();
							}
						}, 1000);
					} else if (!entry.isIntersecting && timer) {
						clearTimeout(timer);
						timer = null;
					}
				}
			},
			{ threshold: 0.5 },
		);

		observer.observe(adContainer.value);
	},
	{ flush: "post" },
);
</script>

<style lang="scss">
@use "../../assets/colors" as *;
@use "../../assets/mixins" as *;
.nav-link {
	color: #fff;
	font-size: 20px;
	font-style: normal;
	height: 24px;
	font-weight: 400;
	line-height: 40px;
	letter-spacing: 0.44px;
	position: relative;
	padding: 0 16px;
	visibility: visible;
}

@keyframes pulse-red {
	0% {
		transform: scale(0.95);
		box-shadow: 0 0 0 0 rgba(255, 82, 82, 0.7);
	}

	70% {
		transform: scale(1);
		box-shadow: 0 0 0 10px transparent;
	}

	100% {
		transform: scale(0.95);
		box-shadow: 0 0 0 0 transparent;
	}
}

.blob {
	background: #000;
	border-radius: 50%;
	box-shadow: 0 0 0 0 #000;
	margin: 14px 8px 15px 12px;
	min-height: 10px;
	height: 10px;
	min-width: 10px;
	width: 10px;
	display: block;
	content: "";
	transform: scale(1);
	animation: pulse-black 2s infinite;

	&.red {
		background: #ff5252;
		box-shadow: 0 0 0 0 #ff5252;
		animation: pulse-red 2s infinite;
	}
}

.nav-link.live {
	@include breakpoint(lg) {
		align-items: center;
		background: #b91c1c;
		border-radius: 8px;
		color: #fff;
		font-size: 14px;
		font-weight: 600;
		gap: 8px;
		line-height: 1;
		height: 40px;
		white-space: nowrap;
		margin-left: 16px;
		padding: 4px 12px 4px 0;
	}
}

.search-btn:hover {
	path {
		fill: $yellow-500;
	}
}

.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}
.fade-enter-active .search-bar,
.fade-leave-active .search-bar {
	transition: transform 0.5s ease;
}

.fade-enter-from .search-bar,
.fade-leave-to .search-bar {
	transform: translateY(-32px);
}

.slide-left-enter-active,
.slide-left-leave-active {
	transition: left 0.25s linear;
}

.slide-left-enter-from,
.slide-left-leave-to {
	left: -100%;
}
</style>
